import MigrateAccountContext, { EMigrateAccountUiState } from '../../contexts/MigrateAccountContext';
import GlobalContext from '../../contexts/GlobalContext';
import { Alert, MenuItem, TextField, Typography, Box, Link } from '@mui/material';
import { FC, SyntheticEvent, useContext, useEffect } from 'react';
import ScreenHeader from '../ScreenHeader/ScreenHeader';
import PageLayoutV2 from '../../layout/PageLayoutV2/PageLayoutV2';
import PasswordInput from '../PasswordInput/PasswordInput';
import PasswordValidator from '../PasswordValidator/PasswordValidator';
import { LoadingButton } from '@mui/lab';
import mixpanel from 'mixpanel-browser';
import { EMixpanelEvent } from '../../util/constants';
import LegalAgreements from '../LegalAgreements/LegalAgreements';
const MigrateAccountForm: FC = () => {
    const { language } = useContext(GlobalContext);
    const { countriesList } = useContext(GlobalContext);
    const {
        migrateAccountUiState,
        firstName,
        lastName,
        email,
        setEmail,
        emailIsValid,
        emailValidationMessage,
        countryCode,
        setCountryCode,
        countryIsValid,
        errorMessage,
        password,
        setPassword,
        passwordIsValid,
        submitMigrateAccount,
    } = useContext(MigrateAccountContext);

    const handleSubmitMigrateAccount = async (event: SyntheticEvent) => {
        event.preventDefault();
        mixpanel.track(EMixpanelEvent.interacted, { key: 'consumer_migration_confirm_info' });
        await submitMigrateAccount();
    };

    useEffect(() => {
        migrateAccountUiState === EMigrateAccountUiState.ready &&
            mixpanel.track(EMixpanelEvent.viewed, { key: 'consumer_migration_confirm_info' });
    }, [migrateAccountUiState]);

    return (
        <PageLayoutV2 testId="migrate-account">
            <ScreenHeader title={language.accountMigration.title} desc={language.accountMigration.desc} />
            {migrateAccountUiState === EMigrateAccountUiState.error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {errorMessage}
                </Alert>
            )}
            <form>
                <TextField
                    label={language.createAccount.form.email}
                    value={email}
                    id="email"
                    size="small"
                    sx={{ mb: 3 }}
                    fullWidth
                    onChange={(event) => setEmail(event.target.value)}
                    error={!emailIsValid}
                    helperText={!emailIsValid && emailValidationMessage}
                    autoFocus={!email ? true : undefined}
                />
                <TextField
                    label={language.createAccount.form.firstName}
                    value={firstName}
                    id="firstName"
                    size="small"
                    sx={{ mb: 1 }}
                    fullWidth
                    disabled
                />
                <TextField
                    label={language.createAccount.form.lastName}
                    value={lastName}
                    id="lastName"
                    size="small"
                    sx={{ mb: 3 }}
                    fullWidth
                    disabled
                />
                <Typography component={'h3'} variant={'subtitle2'} sx={{ mb: 2 }}>
                    {language.accountMigration.password}
                </Typography>
                <PasswordInput
                    value={password}
                    error={!passwordIsValid}
                    label={language.createAccount.form.password}
                    onChange={(event) => setPassword(event.target.value)}
                    helperText={!passwordIsValid && language.validation.password.invalid}
                    id="password"
                    size="small"
                    sx={{ mb: 3 }}
                    autoFocus={email ? true : undefined}
                />
                <Box sx={{ mb: 3 }} data-testid="password-validator">
                    <PasswordValidator password={password} />
                </Box>
                <TextField
                    select
                    label={language.createAccount.form.country}
                    value={countryCode}
                    onChange={(event) => {
                        setCountryCode(event.target.value);
                    }}
                    error={!countryIsValid}
                    size="small"
                    fullWidth
                    sx={{ mb: 3 }}
                >
                    {countriesList.map((country) => (
                        <MenuItem key={country.code} value={country.code}>
                            {country.name}
                        </MenuItem>
                    ))}
                </TextField>
                <LoadingButton
                    variant="contained"
                    size="large"
                    onClick={handleSubmitMigrateAccount}
                    type="submit"
                    fullWidth
                    loading={migrateAccountUiState === EMigrateAccountUiState.loading}
                    disabled={migrateAccountUiState === EMigrateAccountUiState.loading}
                >
                    {language.accountMigration.cta}
                </LoadingButton>
                <Box sx={{ my: 2 }}>
                    <LegalAgreements />
                </Box>
            </form>
        </PageLayoutV2>
    );
};

export default MigrateAccountForm;
